import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  Suspense,
  lazy,
} from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { useModal } from 'react-brave-modal';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { InputCode } from 'components/InputCode';
import { useCurrentCallback } from '../../../lib/use-current-effect';

import Loading from '../../../components/Loading';
import Banner from '../../../components/LoginRegisterBanner';
import Input from '../../../components/Input';
import InputPassword from '../../../components/InputPassword';
import InputCheck from '../../../components/InputCheckAlternative';
import Button from '../../../components/Button';
import getValidationErrors from '../../../utils/getValidationErrors';
import { useAuth } from '../../../hooks/auth';
import history from '../../../services/history';
import changePageAfterLoginRegister from '../../../utils/changePageAfterLoginRegister';

import CincoLogo from '../../../assets/images/cincoLogo1.svg';
import {
  Container,
  Header,
  Content,
  ContentArea,
  CheckBox,
  ContentCodeArea,
} from './styles';

const FailedPasswordAttemptsModal = lazy(
  () =>
    import(
      /* webpackChunkName: "FailedPasswordAttemptsModal" */ '../../../modals/FailedPasswordAttemptsModal'
    ),
);

interface RegisterFormData {
  username: string;
  password: string;
  extendedSession: boolean;
}

interface CodeFormData {
  code: string;
}

const SignIn: React.FC = () => {
  const hasResponse = (
    e: unknown,
  ): e is { response: { data?: { message?: string } } } => {
    return typeof e === 'object' && e !== null && 'response' in e;
  };
  const { showModal } = useModal();
  const formRef = useRef<FormHandles>(null);
  const {
    signIn,
    verifyCodeMFA,
    user: userAuth,
    passwordAttemptsFailed,
    resetPasswordAttempts,
  } = useAuth();
  const [completed, setCompleted] = useState<boolean>(true);
  const [displayCodeInput, setDisplayCodeInput] = useState<boolean>(false);
  const [isExtendedSession, setIsExtendedSession] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [mfaToken, setMfaToken] = useState<string>('');

  useEffect(() => {
    const hasToken = localStorage.getItem('@Cinco:token');

    if (userAuth && hasToken) {
      changePageAfterLoginRegister({
        user: userAuth,
      });
    }
  }, [userAuth]);

  useEffect(() => {
    if (passwordAttemptsFailed) {
      showModal &&
        showModal({
          data: (
            <Suspense fallback={false}>
              <FailedPasswordAttemptsModal />
            </Suspense>
          ),
          type: 'custom',
        });
    }

    resetPasswordAttempts();
  }, [passwordAttemptsFailed, resetPasswordAttempts, showModal]);

  const loginSubmit = useCurrentCallback(
    (isCurrent) => async (data) => {
      setCompleted(false);
      const { mfaToken: mfaTokenRequested } = await signIn({
        ...data,
      });

      if (isCurrent()) {
        setCompleted(true);
      }

      setMfaToken(mfaTokenRequested);
    },
    [signIn],
  );

  const handleCodeSubmit = useCallback(
    async (data: CodeFormData) => {
      try {
        setCompleted(false);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          code: Yup.string()
            .required('Informe o código de validação.')
            .matches(/^\d+$/, 'O código deve conter apenas números.')
            .length(6, 'O código deve ter 6 dígitos.'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { user, token } = await verifyCodeMFA({
          ...data,
          mfaToken,
          extendedSession: isExtendedSession,
          username,
        });

        setMfaToken('');

        if (token) {
          setCompleted(true);
          changePageAfterLoginRegister({ user });
        }
      } catch (err) {
        setCompleted(true);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else if (hasResponse(err)) {
          toast.error(err.response.data?.message);
        } else {
          toast.error('Entre em contato com o administrador.');
        }
      }
    },
    [verifyCodeMFA, mfaToken, isExtendedSession, username],
  );

  const handleSubmit = useCallback(
    async (data: RegisterFormData, _, event) => {
      event.preventDefault();

      try {
        data.username = data.username.trim().toLowerCase();
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          username: Yup.string()
            .required('Informe seu nome de usuário de login.')
            .test(
              'space',
              'Digite um username sem espaço',
              (value) => value.indexOf(' ') < 0,
            ),
          password: Yup.string().min(6, 'Mínimo de 6 dígitos'),
          extendedSession: Yup.boolean(),
        });

        setIsExtendedSession(data.extendedSession);

        await schema.validate(data, {
          abortEarly: false,
        });

        setUsername(data.username);

        await loginSubmit({
          username: data.username,
          password: data.password,
        });

        setDisplayCodeInput(true);
      } catch (err) {
        setDisplayCodeInput(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else if (hasResponse(err)) {
          toast.error(err.response.data?.message);
        } else {
          toast.error('Entre em contato com o administrador.');
        }
      }
    },
    [loginSubmit],
  );

  if (!completed) {
    return <Loading />;
  }

  return (
    <Container>
      <Banner currentPage="signIn" isDoctor />

      <Content>
        <Header>
          <img src={CincoLogo} alt="cinco" />
        </Header>

        {!displayCodeInput && (
          <ContentArea>
            <Form ref={formRef} initialData={{}} onSubmit={handleSubmit}>
              <h1>Entrar</h1>

              <Input name="username" title="Nome de usuário" />
              <InputPassword name="password" title="Insira sua senha" />
              <CheckBox>
                <InputCheck name="extendedSession" />
                <span>Lembrar senha</span>
              </CheckBox>

              <Button type="submit" buttonType="secondary">
                Entrar
                <MdKeyboardArrowRight size={22} />
              </Button>
            </Form>

            <button
              type="button"
              onClick={() => history.push('/password/forgot')}
            >
              Esqueceu sua senha?
            </button>
          </ContentArea>
        )}

        {displayCodeInput && (
          <ContentCodeArea>
            <div>
              <h2>Código de validação</h2>
              <span>Insira o código enviado por E-mail</span>
            </div>
            {completed && (
              <Form ref={formRef} initialData={{}} onSubmit={handleCodeSubmit}>
                <InputCode name="code" />
                <Button type="submit" buttonType="secondary">
                  CONFIRMAR CÓDIGO
                  <MdKeyboardArrowRight size={22} />
                </Button>
              </Form>
            )}
          </ContentCodeArea>
        )}
      </Content>
    </Container>
  );
};

export default SignIn;
